import { useEffect, useRef, useState } from 'react';
import { Elements } from '@/components';
import { useNavigate } from 'react-router';
import { USStateNames } from '@/utils/constants';
import { buildShopPlansStatePath } from '@/lib/shopPlansRouting';

declare global {
    interface Window {
        maplibregl: any;
    }
}

interface StateSummaryData {
    carriers: number;
    onExchangePlans: number;
    offExchangePlans: number;
}

export const StateMap = () => {
    const navigate = useNavigate();
    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef<any>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const hoveredStateIdRef = useRef<string | null>(null);
    const stateDataRef = useRef<Record<string, StateSummaryData>>({});
    const cleanupInteractionsRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        // Start preloading state data immediately when component mounts (in background)
        // Не блокируем инициализацию карты, чтобы она показалась как можно раньше
        preloadAllStateData().catch((error) => {
            console.error('Error preloading state data:', error);
        });

        // Load MapLibre GL CSS
        const cssLink = document.createElement('link');
        cssLink.href = 'https://unpkg.com/maplibre-gl@3.6.2/dist/maplibre-gl.css';
        cssLink.rel = 'stylesheet';
        document.head.appendChild(cssLink);

        // Load MapLibre GL JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/maplibre-gl@3.6.2/dist/maplibre-gl.js';
        script.async = true;
        script.onload = () => {
            setMapLoaded(true);
        };
        document.body.appendChild(script);

        return () => {
            // Cleanup
            if (document.head.contains(cssLink)) {
                document.head.removeChild(cssLink);
            }
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
            // Map cleanup will be handled in the second useEffect
        };
    }, []);

    useEffect(() => {
        // Ждём только загрузки библиотеки карты, данные по штатам догружаются в фоне
        if (!mapLoaded || !mapContainer.current || !window.maplibregl) {
            return;
        }

        // Create tooltip
        const tooltip = document.createElement('div');
        tooltip.className = 'map-tooltip';
        tooltip.style.display = 'none';
        document.body.appendChild(tooltip);
        tooltipRef.current = tooltip;

        // Initialize map
        const map = new window.maplibregl.Map({
            container: mapContainer.current,
            style: {
                version: 8,
                sources: {},
                layers: [],
            },
            center: [-96.5, 30.8],
            zoom: 1,
        });

        map.scrollZoom.disable();
        mapRef.current = map;

        map.on('load', () => {
            const cleanup = loadMapLayers(map, tooltip);
            cleanupInteractionsRef.current = cleanup;

            // Fit bounds to continental US after layers are loaded
            // Wait a bit for layers to render
            setTimeout(() => {
                // Set bounds to continental US (excluding Alaska and Puerto Rico)
                // Bounds: [west, south, east, north]
                map.fitBounds(
                    [
                        [-125, 24.5], // Southwest corner (west, south)
                        [-66, 49.5]   // Northeast corner (east, north)
                    ],
                    {
                        padding: { top: 20, bottom: 20, left: 20, right: 20 },
                        duration: 0 // Instant fit
                    }
                );

                // Set max bounds to prevent panning outside continental US
                map.setMaxBounds([
                    [-127, 24],
                    [-65, 50]
                ]);
            }, 100);
        });

        return () => {
            if (cleanupInteractionsRef.current) {
                cleanupInteractionsRef.current();
                cleanupInteractionsRef.current = null;
            }
            if (tooltip && document.body.contains(tooltip)) {
                document.body.removeChild(tooltip);
            }
            if (mapRef.current) {
                try {
                    // Check if map is still valid before removing
                    if (mapRef.current.getContainer && mapRef.current.getContainer()) {
                        mapRef.current.remove();
                    }
                } catch (error) {
                    // Map might already be removed, ignore error
                    console.warn('Map cleanup error:', error);
                }
                mapRef.current = null;
            }
            hoveredStateIdRef.current = null;
        };
    }, [mapLoaded]);

    const loadMapLayers = (map: any, tooltip: HTMLDivElement) => {
        map.addSource('us_states', {
            type: 'geojson',
            data: 'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json',
            generateId: true,
        });

        map.addLayer({
            id: 'state-fills',
            type: 'fill',
            source: 'us_states',
            paint: {
                'fill-color': [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    '#0077DF',
                    '#E2E8F0',
                ],
                'fill-outline-color': '#FFFFFF',
                'fill-opacity': 1,
            },
        });

        map.addLayer({
            id: 'state-borders',
            type: 'line',
            source: 'us_states',
            paint: {
                'line-color': '#FFFFFF',
                'line-width': 1,
            },
        });

        return setupInteractions(map, tooltip, mapContainer.current);
    };

    const getStateAbbreviation = (stateName: string): string => {
        // Find state abbreviation from USStateNames
        for (const [abbr, name] of Object.entries(USStateNames)) {
            if (name.toLowerCase() === stateName.toLowerCase()) {
                return abbr;
            }
        }
        return '';
    };

    const preloadAllStateData = async () => {
        const response = await fetch('https://rates-public.predictablebenefits.com/public/plans/summary');
        if (!response.ok) {
            throw new Error('Failed to fetch state data');
        }
        const data = await response.json();

        // Preload data for all states
        for (const [abbr, stateName] of Object.entries(USStateNames)) {
            if (data[abbr]) {
                const stateData = data[abbr];
                const summary: StateSummaryData = {
                    carriers: stateData.carrier_count || 0,
                    onExchangePlans: stateData.on_exchange_plans || 0,
                    offExchangePlans: stateData.off_exchange_plans || 0,
                };
                stateDataRef.current[stateName as string] = summary;
            }
        }
    };

    const setupInteractions = (map: any, tooltip: HTMLDivElement, container: HTMLDivElement | null) => {
        const navigateRef = navigate;
        let currentStateName: string | null = null;
        let buttonClickHandler: ((event: Event) => void) | null = null;
        let hideTooltipTimeout: number | null = null;
        let isMouseOverTooltip = false;
        let positionUpdateFrame: number | null = null;

        const updateTooltipContent = (stateName: string) => {
            // Only update content if state changed
            if (currentStateName === stateName && tooltip.innerHTML) {
                return;
            }

            currentStateName = stateName;

            // Remove old click handler if exists
            if (buttonClickHandler) {
                const oldLink = tooltip.querySelector('.tooltip-link');
                if (oldLink) {
                    oldLink.removeEventListener('click', buttonClickHandler);
                }
                buttonClickHandler = null;
            }

            const data: StateSummaryData | null = stateDataRef.current[stateName] || null;

            const stateAbbr = getStateAbbreviation(stateName);
            const stateCode = stateAbbr || stateName.toUpperCase();

            if (!data) {
                tooltip.innerHTML = '';
                currentStateName = null;
                tooltip.style.display = 'none';
                return;
            }

            tooltip.innerHTML = `
                <div class="tooltip-content">
                    <span class="tooltip-title">${stateName}</span>
                    <div class="tooltip-divider"></div>
                    <div class="tooltip-row">
                        <span class="tooltip-label">Carriers</span>
                        <span class="tooltip-value">${data.carriers}</span>
                    </div>
                    <div class="tooltip-row">
                        <span class="tooltip-label">On-Exchange Plans</span>
                        <span class="tooltip-value">${data.onExchangePlans}</span>
                    </div>
                    <div class="tooltip-row">
                        <span class="tooltip-label">Off-Exchange Plans</span>
                        <span class="tooltip-value">${data.offExchangePlans}</span>
                    </div>
                        <span class="tooltip-link" data-state-code="${stateCode}">Click to browse plans</span>
                </div>
            `;

            // Add click handler for "Click to browse plans" link
            const link = tooltip.querySelector('.tooltip-link');
            if (link) {
                buttonClickHandler = (event: Event) => {
                    event.stopPropagation();
                    const stateCode = link.getAttribute('data-state-code');
                    if (stateCode) {
                        navigateRef(buildShopPlansStatePath(stateCode));
                    }
                };
                link.addEventListener('click', buttonClickHandler);
            }
        };

        map.on('mousemove', 'state-fills', (e: any) => {
            if (e.features.length > 0) {
                const currentStateId = e.features[0].id;
                const stateName = e.features[0].properties.name;
                const hasData = Boolean(stateDataRef.current[stateName]);

                // Only show tooltip when we have data for this state
                if (!hasData) {
                    tooltip.style.display = 'none';
                } else {
                    tooltip.style.display = 'block';
                }

                // Update position immediately
                if (container) {
                    const rect = container.getBoundingClientRect();
                    tooltip.style.position = 'fixed';
                    tooltip.style.left = (rect.left + e.point.x + 20) + 'px';
                    tooltip.style.top = (rect.top + e.point.y + 20) + 'px';
                } else {
                    tooltip.style.position = 'fixed';
                    tooltip.style.left = (e.point.x + 20) + 'px';
                    tooltip.style.top = (e.point.y + 20) + 'px';
                }

                // Update hover state only if state changed
                if (hoveredStateIdRef.current !== currentStateId) {
                    if (hoveredStateIdRef.current !== null) {
                        map.setFeatureState(
                            { source: 'us_states', id: hoveredStateIdRef.current },
                            { hover: false }
                        );
                    }
                    hoveredStateIdRef.current = currentStateId;
                    map.setFeatureState(
                        { source: 'us_states', id: hoveredStateIdRef.current },
                        { hover: true }
                    );

                    // Update tooltip content asynchronously (won't block display)
                    if (hasData) {
                        updateTooltipContent(stateName);
                    }
                }

                // Update position smoothly on every mouse move
                if (container) {
                    const rect = container.getBoundingClientRect();
                    tooltip.style.left = (rect.left + e.point.x + 20) + 'px';
                    tooltip.style.top = (rect.top + e.point.y + 20) + 'px';
                } else {
                    tooltip.style.left = (e.point.x + 20) + 'px';
                    tooltip.style.top = (e.point.y + 20) + 'px';
                }
            }
        });

        // Keep tooltip visible when mouse is over it
        tooltip.addEventListener('mouseenter', () => {
            isMouseOverTooltip = true;
            if (hideTooltipTimeout !== null) {
                clearTimeout(hideTooltipTimeout);
                hideTooltipTimeout = null;
            }
        });

        tooltip.addEventListener('mouseleave', () => {
            isMouseOverTooltip = false;
            hideTooltip();
        });

        const hideTooltip = () => {
            if (hideTooltipTimeout !== null) {
                clearTimeout(hideTooltipTimeout);
            }
            // Minimal delay to allow moving mouse to tooltip
            hideTooltipTimeout = window.setTimeout(() => {
                if (!isMouseOverTooltip) {
                    if (hoveredStateIdRef.current !== null) {
                        map.setFeatureState(
                            { source: 'us_states', id: hoveredStateIdRef.current },
                            { hover: false }
                        );
                    }
                    hoveredStateIdRef.current = null;
                    currentStateName = null;
                    tooltip.style.display = 'none';
                }
            }, 50); // Minimal delay (50ms) to allow moving mouse to tooltip
        };

        // Add click handler for states
        map.on('click', 'state-fills', (e: any) => {
            if (e.features.length > 0) {
                const stateName = e.features[0].properties.name;
                const stateAbbr = getStateAbbreviation(stateName);
                const stateCode = stateAbbr || stateName.toUpperCase();
                navigateRef(buildShopPlansStatePath(stateCode));
            }
        });

        map.on('mouseleave', 'state-fills', () => {
            // Cancel pending position update
            if (positionUpdateFrame !== null) {
                cancelAnimationFrame(positionUpdateFrame);
                positionUpdateFrame = null;
            }

            if (!isMouseOverTooltip) {
                hideTooltip();
            }
        });

        // Cleanup function
        return () => {
            // Cleanup handled by map removal
        };
    };

    return (
        <div className="w-full max-w-[1440px] mx-auto">
            <div className="mb-6 text-center">
                <Elements.Title
                    title="Select a state to browse available plans"
                    type="h2"
                    className="text-center mb-2"
                />
                <Elements.Description
                    description="Click on a state to see carrier details and plan options"
                    type="medium"
                    className="text-center text-gray-600"
                />
            </div>
            <div className="aspect-[1450/775] w-full relative">
                <div ref={mapContainer} className="w-full h-full rounded-lg" />
                <style>{`
                .map-tooltip {
                    display: none;
                    position: fixed;
                    background-color: #F1F8FE;
                    padding: 24px;
                    border-radius: 16px;
                    pointer-events: auto;
                    z-index: 1000;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.25);
                    width: 275px;
                    line-height: 1.5;
                }
                .map-tooltip .tooltip-content {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }
                .map-tooltip .tooltip-data {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .map-tooltip .tooltip-title {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 400;
                    color: #25272C;
                }
                .map-tooltip .tooltip-label {
                    font-size: 16px;
                    font-weight: 400;
                    color: #0967C4;
                }
                .map-tooltip .tooltip-value {
                    font-size: 16px;
                    font-weight: 400;
                    color: #0967C4;
                }
                .map-tooltip .tooltip-divider {
                    width: 100%;
                    height: 1px;
                    background-color: #E5E7EB;
                    margin: 0;
                }
                .map-tooltip .tooltip-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 14px;
                    color: #4B5563;
                }
                .map-tooltip .tooltip-link {
                    margin-top: 8px;
                    color: #0077DF;
                    font-size: 14px;
                    font-weight: 700;
                    font-style: italic;
                    cursor: pointer;
                    text-decoration: none;
                    transition: color 0.2s;
                }
                .map-tooltip .tooltip-link:hover {
                    color: #0056B3;
                    text-decoration: underline;
                }
            `}</style>
            </div>
        </div>
    );
};
